import {
  ClientApplicationForTableFragment,
  useClientApplicationsForTableQuery,
} from "@graphql/client-applications/ClientApplications.g";
import {
  TenantedClientApplicationManagedByChoices,
  TenantedClientApplicationReviewStateChoices,
  TenantedClientApplicationSupportedByChoices,
} from "@graphql/generated/graphql.g";
import { keepPreviousData } from "@tanstack/react-query";
import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import {
  ClientApplicationsTableConfig,
  renderBusinessOwner,
  renderCategory,
  renderCreatedAt,
  renderIsVisible,
  renderLastUsed,
  renderManagedBy,
  renderNotes,
  renderNotifications,
  renderProductDescription,
  renderProductName,
  renderReviewState,
  renderSupportedBy,
  renderUpdatedAt,
  renderUsedBy,
  renderVendorDescription,
  renderVendorName,
} from "./ClientApplicationsTableConfig";
import { ClientApplicationReviewPanel } from "./ClientApplicationReviewPanel/ClientApplicationReviewPanel";
import { ClientApplicationTableColumns } from "./UI/ClientApplicationsTableColumns";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;
const PRODUCT_NAME_SEARCH_DEBOUNCE_TIME = 300;

const columnHelper = createColumnHelper<ClientApplicationForTableFragment>();

type Props = {
  clientId: string;
  initialPageIndex?: number;
  search?: string;
  reviewState?: TenantedClientApplicationReviewStateChoices[];
  managedBy?: TenantedClientApplicationManagedByChoices[];
  supportedBy?: TenantedClientApplicationSupportedByChoices[];
  businessOwnerId?: string;
  productNameSearch?: string;
  parentCategoryId?: string;
  createdAt_Gte?: string;
  createdAt_Lte?: string;
  isVisibleFilter?: "all" | "visible" | "hidden";
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
};

const DEFAULT_SORTING: SortingState = [
  { id: ClientApplicationTableColumns.productName, desc: false },
];

export const ClientApplicationsTable = ({
  clientId,
  initialPageIndex = 0,
  search,
  reviewState,
  managedBy,
  supportedBy,
  businessOwnerId,
  productNameSearch,
  parentCategoryId,
  createdAt_Gte,
  createdAt_Lte,
  isVisibleFilter = "visible",
  sorting: controlledSorting,
  onSortingChange,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [internalSorting, setInternalSorting] =
    useState<SortingState>(DEFAULT_SORTING);
  const sorting = controlledSorting ?? internalSorting;

  // Custom sorting handler that toggles between ASC and DESC only (no unsorted state)
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;

      // If trying to clear sorting (empty array), toggle direction instead
      const resolvedSorting =
        newSorting.length === 0 && sorting.length > 0
          ? [
              {
                id: sorting[0].id,
                desc: !sorting[0].desc,
              },
            ]
          : newSorting;

      if (onSortingChange) {
        onSortingChange(resolvedSorting);
      } else {
        setInternalSorting(resolvedSorting);
      }
    },
    [sorting, onSortingChange],
  );
  const [selectedApplicationForReview, setSelectedApplicationForReview] =
    useState<ClientApplicationForTableFragment | null>(null);
  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);
  const debouncedProductNameSearch = useDebounce(
    productNameSearch,
    PRODUCT_NAME_SEARCH_DEBOUNCE_TIME,
  );
  const debouncedIsVisibleFilter = useDebounce(
    isVisibleFilter,
    SEARCH_DEBOUNCE_TIME,
  );

  const visibilityQueryArgs = useMemo(() => {
    if (!debouncedIsVisibleFilter || debouncedIsVisibleFilter === "visible") {
      return { resource_IsVisible: true as boolean | undefined };
    }
    if (debouncedIsVisibleFilter === "hidden") {
      return { resource_IsVisible: false as boolean | undefined };
    }
    // "all" — opt out of the API's default visible-only gate
    return { includeHidden: true as boolean | undefined };
  }, [debouncedIsVisibleFilter]);

  // Use client applications query with primary resources only
  const clientApplicationsQuery = useClientApplicationsForTableQuery(
    {
      clientId,
      first: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      orderBy: ConfigurableTableSystem.generateSortParameter(
        ClientApplicationsTableConfig.columns,
        sorting,
      ),
      reviewState_In: reviewState,
      managedBy_In: managedBy,
      supportedBy_In: supportedBy,
      businessOwner_In: businessOwnerId ? [businessOwnerId] : undefined,
      resource_ProductName_Icontains: debouncedProductNameSearch || undefined,
      parentCategoryId: parentCategoryId || undefined,
      createdAt_Gte: createdAt_Gte || undefined,
      createdAt_Lte: createdAt_Lte || undefined,
      ...visibilityQueryArgs,
    },
    {
      // Keep previous data while fetching to avoid showing "no results"
      placeholderData: keepPreviousData,
    },
  );
  const refetchClientApplications = clientApplicationsQuery.refetch;
  const navigate = useNavigate();
  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isClientApplicationsLoading = clientApplicationsQuery.isLoading;
  const isClientApplicationsFetching = clientApplicationsQuery.isFetching;

  // Extract applications from query response
  const data = useMemo(() => {
    const edges = clientApplicationsQuery.data?.clientApplications?.edges ?? [];
    return edges
      .map((edge) => edge?.node)
      .filter((app): app is ClientApplicationForTableFragment => app !== null);
  }, [clientApplicationsQuery.data]);

  const totalCount =
    clientApplicationsQuery.data?.clientApplications?.totalCount ?? 0;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [
    debouncedSearch,
    reviewState,
    managedBy,
    supportedBy,
    businessOwnerId,
    debouncedProductNameSearch,
    parentCategoryId,
    createdAt_Gte,
    createdAt_Lte,
    debouncedIsVisibleFilter,
  ]);

  const handleApplicationDashboardClick = useCallback(
    (applicationId: string) => {
      // Navigate to dashboard with the application ID
      navigate(
        routes.portal.clients.client.applications.application.dashboard.$buildPath(
          {
            params: {
              clientId,
              applicationId,
            },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const handleUsersClick = useCallback(
    (applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.users.$buildPath({
          params: {
            clientId,
            applicationId,
          },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const handleReviewClick = useCallback(
    (application: ClientApplicationForTableFragment) => {
      setSelectedApplicationForReview(application);
    },
    [],
  );

  const handleCloseReviewPanel = useCallback(() => {
    setSelectedApplicationForReview(null);
  }, []);

  const handleReviewSuccess = useCallback(() => {
    refetchClientApplications();
  }, [refetchClientApplications]);

  const configuredColumns = useMemo(() => {
    // Create column configuration with custom renderers
    const configWithRenderers = {
      ...ClientApplicationsTableConfig,
      columns: Object.fromEntries(
        Object.entries(ClientApplicationsTableConfig.columns).map(
          ([key, config]) => [
            key,
            {
              ...config,
              render: (
                value: unknown,
                row: ClientApplicationForTableFragment,
              ) => {
                const columnKey = key as ClientApplicationTableColumns;
                // Get aggregated stats from resourceGroupApplications
                const stats = row.resourceGroupApplications?.stats;

                // Map to the appropriate render function
                switch (columnKey) {
                  case ClientApplicationTableColumns.productName:
                    // Use the standard product name renderer
                    return renderProductName(
                      row.resource?.productName,
                      row,
                      handleApplicationDashboardClick,
                    );
                  case ClientApplicationTableColumns.isVisible:
                    return renderIsVisible(row.resource?.isVisible ?? false);
                  case ClientApplicationTableColumns.reviewState:
                    // Show application's review state
                    return renderReviewState(row.reviewState);
                  case ClientApplicationTableColumns.managedBy:
                    // Show application's managed by
                    return renderManagedBy(row.managedBy);
                  case ClientApplicationTableColumns.supportedBy:
                    // Show application's supported by
                    return renderSupportedBy(row.supportedBy);
                  case ClientApplicationTableColumns.usedBy:
                    // Distinct-user count; links to this application's user list
                    return renderUsedBy(
                      stats?.totalEmployees ?? 0,
                      row,
                      handleUsersClick,
                    );
                  case ClientApplicationTableColumns.category: {
                    // Get parent category name, fallback to category name
                    const parentName =
                      row.resource?.publicResource?.category?.parent?.name;
                    const categoryName =
                      row.resource?.publicResource?.category?.name;
                    return renderCategory(parentName || categoryName);
                  }
                  case ClientApplicationTableColumns.vendorName:
                    return renderVendorName(row.resource?.vendorName);
                  case ClientApplicationTableColumns.businessOwner:
                    return renderBusinessOwner(row.businessOwner?.name || "—");
                  case ClientApplicationTableColumns.createdAt:
                    return renderCreatedAt(row.createdAt);
                  case ClientApplicationTableColumns.lastUsed:
                    return renderLastUsed(stats?.lastSessionTime);
                  case ClientApplicationTableColumns.updatedAt:
                    return renderUpdatedAt(row.updatedAt);
                  case ClientApplicationTableColumns.notes:
                    // Show notes value
                    return renderNotes(row.notes);
                  case ClientApplicationTableColumns.notifications:
                    // Use aggregated total notifications from stats
                    return renderNotifications(stats?.totalNotifications ?? 0);
                  case ClientApplicationTableColumns.productDescription:
                    return renderProductDescription(
                      row.resource?.productDescription,
                    );
                  case ClientApplicationTableColumns.vendorDescription:
                    return renderVendorDescription(
                      row.resource?.vendorDescription,
                    );
                  default:
                    return String(value);
                }
              },
            },
          ],
        ),
      ) as typeof ClientApplicationsTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as ClientApplicationTableColumns[])
            : undefined,
        columnVisibility: columnSettings,
      },
    );
  }, [
    data,
    columnOrder,
    columnSettings,
    handleApplicationDashboardClick,
    handleUsersClick,
  ]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<ClientApplicationForTableFragment, unknown>[] = [
      ...configuredColumns,
    ];

    // Always add the actions column at the end
    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          return (
            <Box gap={2}>
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Dashboard"
                  icon="faGrip"
                  onSelect={() =>
                    handleApplicationDashboardClick(props.row.original.id)
                  }
                />
                <Dropdown.Option
                  label="Review"
                  icon="faPencil"
                  onSelect={() => handleReviewClick(props.row.original)}
                />
                <Dropdown.Option
                  label="Users"
                  icon="faUser"
                  onSelect={() => handleUsersClick(props.row.original.id)}
                />
              </Dropdown>
            </Box>
          );
        },
      }),
    );

    return columns;
  }, [
    configuredColumns,
    handleApplicationDashboardClick,
    handleReviewClick,
    handleUsersClick,
  ]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <>
      <Table<ClientApplicationForTableFragment, string>
        columns={allColumnsWithActions}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={isClientApplicationsLoading}
        isFetching={isClientApplicationsFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        columnVisibility={columnSettings}
        manualSorting={true}
      />

      <ClientApplicationReviewPanel
        application={selectedApplicationForReview}
        clientId={clientId}
        isOpen={!!selectedApplicationForReview}
        onClose={handleCloseReviewPanel}
        onSuccess={handleReviewSuccess}
      />
    </>
  );
};
